import {
  ArrowTopRightOnSquareIcon,
  BanknotesIcon,
  ChevronDownIcon,
  ClockIcon,
  EnvelopeIcon,
  MapPinIcon,
  PhoneIcon,
} from "@heroicons/react/24/outline";
import type { Company } from "@type/contact";
import useTranslation from "next-translate/useTranslation";

import {
  mapsDirectionsUrl,
  mapsEmbedUrl,
  mapsSearchUrl,
  telHref,
} from "./contact-utils";
import { CopyButton } from "./copy-button";
import { SectionHeading } from "./section-heading";

interface HeadquartersProps {
  company: Company;
}

export const Headquarters = ({ company }: HeadquartersProps) => {
  const { t, lang } = useTranslation("contact");
  const { address, openingHours, registry } = company;

  const hoursRows = [
    { hours: openingHours.weekdays, label: t("hours.weekdays") },
    { hours: openingHours.saturday, label: t("hours.saturday") },
    { hours: openingHours.sunday, label: t("hours.sunday") },
  ];

  const registryRows = [
    { label: "NIP", value: registry.nip },
    { label: "REGON", value: registry.regon },
    { label: "KRS", value: registry.krs },
    { label: "BDO", value: registry.bdo },
    {
      label: t("headquarters.registry.shareCapital"),
      value: registry.shareCapital,
    },
    { label: t("headquarters.registry.court"), value: registry.court },
  ];

  return (
    <section
      aria-labelledby="headquarters-title"
      className="border-gray-100 border-t bg-white py-16 md:py-24"
      id="headquarters"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow={t("headquarters.eyebrow")}
          id="headquarters-title"
          // title={t("headquarters.title")}
        />

        <div className="overflow-hidden rounded-2xl border border-gray-200 shadow-sm">
          <div className="grid lg:grid-cols-[minmax(0,26rem)_1fr]">
            <div className="p-6 md:p-8">
              <p className="font-semibold text-[#8a6d2f] text-xs uppercase tracking-[0.16em]">
                {t("headquarters.plantLabel")}
              </p>
              <h3 className="mt-2 font-bold text-[#051124] text-xl">
                {company.name}
              </h3>

              <div className="mt-6 space-y-5 text-sm">
                <div className="flex gap-3">
                  <MapPinIcon
                    aria-hidden="true"
                    className="size-5 shrink-0 text-[#c1a166]"
                  />
                  <address className="text-gray-700 not-italic leading-relaxed">
                    {address.street}
                    <br />
                    {address.postalCode} {address.city}, {address.country}
                  </address>
                </div>

                <div className="flex gap-3">
                  <ClockIcon
                    aria-hidden="true"
                    className="size-5 shrink-0 text-[#c1a166]"
                  />
                  <dl className="grid flex-1 grid-cols-[1fr_auto] gap-x-4 gap-y-1">
                    {hoursRows.map((row) => (
                      <div className="contents" key={row.label}>
                        <dt className="text-gray-700">{row.label}</dt>
                        <dd
                          className={`text-right ${row.hours ? "font-semibold text-[#051124]" : "text-gray-500"}`}
                        >
                          {row.hours ?? t("hours.closed")}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>

                <div className="flex gap-3">
                  <PhoneIcon
                    aria-hidden="true"
                    className="size-5 shrink-0 text-[#c1a166]"
                  />
                  <div className="space-y-1">
                    <a
                      className="block font-semibold text-[#051124] hover:text-[#8a6d2f]"
                      href={telHref(company.phone)}
                    >
                      {company.phone}
                    </a>
                    <a
                      className="inline-flex items-center gap-1.5 break-all text-[#8a6d2f] hover:underline"
                      href={`mailto:${company.email}`}
                    >
                      <EnvelopeIcon aria-hidden="true" className="size-4" />
                      {company.email}
                    </a>
                  </div>
                </div>
              </div>

              <a
                className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#051124] px-5 py-3 font-semibold text-sm text-white transition hover:bg-[#0a1a33] focus-visible:outline-2 focus-visible:outline-[#c1a166] focus-visible:outline-offset-2 sm:w-auto"
                href={mapsDirectionsUrl(company.mapsQuery)}
                rel="noopener noreferrer"
                target="_blank"
              >
                <MapPinIcon aria-hidden="true" className="size-4" />
                {t("hero.directions")}
              </a>
            </div>

            <div className="relative min-h-72 bg-gray-100 sm:min-h-96">
              <iframe
                className="absolute inset-0 size-full border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                src={mapsEmbedUrl(company.mapsQuery, lang)}
                title={t("headquarters.mapTitle")}
              />
              <a
                className="absolute top-3 right-3 hidden items-center gap-2 rounded-md bg-white px-3.5 py-2 font-semibold text-[#051124] text-xs shadow-lg transition hover:bg-[#f6f4ef] sm:inline-flex"
                href={mapsSearchUrl(company.mapsQuery)}
                rel="noopener noreferrer"
                target="_blank"
              >
                <ArrowTopRightOnSquareIcon
                  aria-hidden="true"
                  className="size-4"
                />
                {t("headquarters.openInMaps")}
              </a>
            </div>
          </div>

          <details className="group border-gray-200 border-t bg-[#fbfaf7]" open>
            <summary className="flex cursor-pointer list-none items-center gap-3 px-6 py-5 md:px-8 [&::-webkit-details-marker]:hidden">
              <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-[#c1a166]/15 text-[#8a6d2f]">
                <BanknotesIcon aria-hidden="true" className="size-5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block font-semibold text-[#051124]">
                  {t("headquarters.registry.title")}
                </span>
                <span className="block text-gray-500 text-xs">
                  {t("headquarters.registry.description")}
                </span>
              </span>
              <ChevronDownIcon
                aria-hidden="true"
                className="size-5 shrink-0 text-gray-500 transition group-open:rotate-180"
              />
            </summary>

            <div className="grid gap-8 px-6 pb-8 md:grid-cols-2 md:px-8 lg:grid-cols-3">
              <div>
                <h4 className="font-semibold text-[11px] text-gray-500 uppercase tracking-[0.14em]">
                  {t("headquarters.registry.registryData")}
                </h4>
                <dl className="mt-3 divide-y divide-gray-100 text-sm">
                  {registryRows.map((row) => (
                    <div
                      className="flex items-center justify-between gap-4 py-2"
                      key={row.label}
                    >
                      <dt className="text-gray-500">{row.label}</dt>
                      <dd className="text-right font-medium text-[#051124]">
                        {row.value}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>

              {company.banks.map((bank) => (
                <div key={bank.id}>
                  <h4 className="font-semibold text-[11px] text-gray-500 uppercase tracking-[0.14em]">
                    {bank.name}
                  </h4>
                  <p className="mt-3 font-medium text-[#051124] text-sm">
                    {bank.branch}
                  </p>
                  <p className="text-gray-500 text-xs">SWIFT: {bank.swift}</p>
                  <dl className="mt-3 divide-y divide-gray-100 text-sm">
                    {bank.accounts.map((account) => (
                      <div
                        className="flex items-center gap-x-3 py-1.5"
                        key={account.number}
                      >
                        {/* Na telefonie waluta nad numerem, aby numer mieścił się w linii */}
                        <div className="flex min-w-0 flex-1 flex-col xl:flex-row xl:items-center xl:gap-3">
                          <dt className="text-gray-500 text-xs xl:w-9 xl:shrink-0 xl:text-sm">
                            {account.currency}
                          </dt>
                          <dd className="whitespace-nowrap font-medium text-[#051124] tabular-nums">
                            {account.number}
                          </dd>
                        </div>
                        <CopyButton value={account.number} />
                      </div>
                    ))}
                  </dl>
                </div>
              ))}
            </div>
          </details>
        </div>
      </div>
    </section>
  );
};
